import { useEffect, useState } from 'react';
import type { CancellationRule, DepositRule, NoShowRule } from '@salonomy/types';
import { Field, Select, TextInput } from '@salonomy/ui';
import { penceToPounds, poundsToPence } from './policyText.js';

/**
 * The three structured rule editors (deposit / cancellation window / no-show), factored out so the
 * salon-level BookingPolicyEditor and the per-service ServicePolicyOverrideModal share ONE source of
 * the inputs. Each block is a controlled editor over its rule object; money is integer pence
 * end-to-end (the £ TextInput is a display convenience, mirroring ServiceModal). Percent is a
 * whole-percent 0–100. These never decide whether auto-charge is armed — that is a salon-wide switch
 * the BookingPolicyEditor owns.
 */

const FEE_BLURB =
  'Forfeit deposit keeps any deposit already paid; fixed is a set amount; percent is a share of the service price.';

const sectionTitle = (children: string) => (
  <div className="section-title" style={{ fontSize: 15, margin: '8px 0 2px' }}>
    {children}
  </div>
);

/** A whole-number percent input with a trailing "%" affordance. */
function PercentInput({
  value,
  onChange,
  ariaLabel,
}: {
  value: number;
  onChange: (next: number) => void;
  ariaLabel: string;
}) {
  return (
    <TextInput
      type="number"
      inputMode="numeric"
      min={0}
      max={100}
      aria-label={ariaLabel}
      value={String(value)}
      onChange={(e) => {
        const n = Math.max(0, Math.min(100, Math.round(Number(e.target.value) || 0)));
        onChange(n);
      }}
      placeholder="0"
    />
  );
}

/**
 * A £ input bound to a pence field (blank ⇒ 0), mirroring ServiceModal's price input. Holds a local
 * display string so the user can type "14." mid-edit, but re-syncs when the bound pence value changes
 * from outside (e.g. the parent re-seeds from a freshly loaded policy) WITHOUT clobbering an active
 * edit — it only reformats when the incoming pence no longer matches what's typed.
 */
function PoundsInput({
  pence,
  onChange,
  ariaLabel,
}: {
  pence: number;
  onChange: (next: number) => void;
  ariaLabel: string;
}) {
  const [text, setText] = useState(() => penceToPounds(pence));
  useEffect(() => {
    if ((poundsToPence(text) ?? 0) !== pence) setText(penceToPounds(pence));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pence]);
  return (
    <TextInput
      inputMode="decimal"
      aria-label={ariaLabel}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        onChange(poundsToPence(e.target.value) ?? 0);
      }}
      placeholder="0.00"
    />
  );
}

// ── Deposit ─────────────────────────────────────────────────────────────────────

export function DepositRuleFields({
  rule,
  onChange,
}: {
  rule: DepositRule;
  onChange: (next: DepositRule) => void;
}) {
  const set = (patch: Partial<DepositRule>) => onChange({ ...rule, ...patch });
  return (
    <div>
      {sectionTitle('Deposit')}
      <div className="muted" style={{ fontSize: 12.5, marginBottom: 8 }}>
        A deposit secures the booking. Off by default — switch it on only if you want it.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="How it's calculated">
          <Select
            value={rule.kind}
            onChange={(e) => set({ kind: e.target.value as DepositRule['kind'] })}
          >
            <option value="none">No deposit</option>
            <option value="fixed">Fixed amount</option>
            <option value="percent">Percent of price</option>
          </Select>
        </Field>
        {rule.kind === 'fixed' ? (
          <Field label="Deposit amount (£)">
            <PoundsInput
              pence={rule.amountPence}
              onChange={(amountPence) => set({ amountPence })}
              ariaLabel="Deposit amount in pounds"
            />
          </Field>
        ) : rule.kind === 'percent' ? (
          <Field label="Deposit (% of price)">
            <PercentInput
              value={rule.percent}
              onChange={(percent) => set({ percent })}
              ariaLabel="Deposit percent of price"
            />
          </Field>
        ) : (
          <div />
        )}
      </div>

      {rule.kind !== 'none' ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 4 }}>
          <Field label="When it's required">
            <Select
              value={rule.when}
              onChange={(e) => set({ when: e.target.value as DepositRule['when'] })}
            >
              <option value="always">Every booking</option>
              <option value="new_clients">New clients only</option>
              <option value="over_threshold">Services over a price</option>
            </Select>
          </Field>
          {rule.when === 'over_threshold' ? (
            <Field label="Price threshold (£)" hint="Only services priced at or above this.">
              <PoundsInput
                pence={rule.overThresholdPence}
                onChange={(overThresholdPence) => set({ overThresholdPence })}
                ariaLabel="Deposit price threshold in pounds"
              />
            </Field>
          ) : (
            <div />
          )}
        </div>
      ) : null}
    </div>
  );
}

// ── Cancellation ──────────────────────────────────────────────────────────────────

export function CancellationRuleFields({
  rule,
  onChange,
}: {
  rule: CancellationRule;
  onChange: (next: CancellationRule) => void;
}) {
  const set = (patch: Partial<CancellationRule>) => onChange({ ...rule, ...patch });
  return (
    <div>
      {sectionTitle('Cancellation window')}
      <div className="muted" style={{ fontSize: 12.5, marginBottom: 8 }}>
        Free to cancel up to a cut-off; cancelling later than that can carry a fee.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="Free until (hours before)" hint="e.g. 24 = free up to a day before.">
          <TextInput
            type="number"
            inputMode="numeric"
            min={0}
            max={8760}
            value={String(rule.freeUntilHours)}
            onChange={(e) =>
              set({
                freeUntilHours: Math.max(
                  0,
                  Math.min(8760, Math.round(Number(e.target.value) || 0)),
                ),
              })
            }
          />
        </Field>
        <Field label="Late-cancel fee" hint={FEE_BLURB}>
          <Select
            value={rule.feeKind}
            onChange={(e) => set({ feeKind: e.target.value as CancellationRule['feeKind'] })}
          >
            <option value="none">No fee</option>
            <option value="forfeit_deposit">Forfeit deposit</option>
            <option value="fixed">Fixed amount</option>
            <option value="percent">Percent of price</option>
          </Select>
        </Field>
      </div>
      {rule.feeKind === 'fixed' ? (
        <Field label="Late-cancel fee (£)">
          <PoundsInput
            pence={rule.amountPence}
            onChange={(amountPence) => set({ amountPence })}
            ariaLabel="Late cancellation fee in pounds"
          />
        </Field>
      ) : rule.feeKind === 'percent' ? (
        <Field label="Late-cancel fee (% of price)">
          <PercentInput
            value={rule.percent}
            onChange={(percent) => set({ percent })}
            ariaLabel="Late cancellation fee percent of price"
          />
        </Field>
      ) : null}
    </div>
  );
}

// ── No-show ─────────────────────────────────────────────────────────────────────

export function NoShowRuleFields({
  rule,
  onChange,
}: {
  rule: NoShowRule;
  onChange: (next: NoShowRule) => void;
}) {
  const set = (patch: Partial<NoShowRule>) => onChange({ ...rule, ...patch });
  return (
    <div>
      {sectionTitle('No-show fee')}
      <div className="muted" style={{ fontSize: 12.5, marginBottom: 8 }}>
        What a missed appointment costs when the client never arrives.
      </div>
      <Field label="No-show fee" hint={FEE_BLURB}>
        <Select
          value={rule.feeKind}
          onChange={(e) => set({ feeKind: e.target.value as NoShowRule['feeKind'] })}
        >
          <option value="none">No fee</option>
          <option value="forfeit_deposit">Forfeit deposit</option>
          <option value="fixed">Fixed amount</option>
          <option value="percent">Percent of price</option>
        </Select>
      </Field>
      {rule.feeKind === 'fixed' ? (
        <Field label="No-show fee (£)">
          <PoundsInput
            pence={rule.amountPence}
            onChange={(amountPence) => set({ amountPence })}
            ariaLabel="No-show fee in pounds"
          />
        </Field>
      ) : rule.feeKind === 'percent' ? (
        <Field label="No-show fee (% of price)">
          <PercentInput
            value={rule.percent}
            onChange={(percent) => set({ percent })}
            ariaLabel="No-show fee percent of price"
          />
        </Field>
      ) : null}
    </div>
  );
}
